import { useState } from 'react'
import { useShallow } from 'zustand/react/shallow'

import type { Operation } from '@fit-editor/fit-core'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { downloadBytes, EXPORT_MIME_TYPES } from '@/lib/download'
import {
  EXPORT_FORMAT_LABELS,
  exportActivity,
  exportFailureOutcome,
  type ExportFormat,
  type ExportOutcome,
} from '@/lib/export-activity'
import { selectBatchSelectedActivities, useActivityStore } from '@/store/activity-store'
import { useNotificationStore } from '@/store/notification-store'
import { bundleFilesToZipAsync, currentActivityAsync } from '@/worker/fit-client'

export interface BatchDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

type EditKind = 'shiftTime' | 'editMetadata'
const FORMATS: ExportFormat[] = ['fit', 'gpx', 'tcx', 'csv', 'geojson']

/**
 * Applies one operation, or exports, to every ticked activity at once
 * (M7's batch processing). Scoped to the two operations whose parameters
 * are meaningful across *different* activities without per-activity
 * customization: `shiftTime` (a fixed offset applies identically to any
 * activity) and `editMetadata` (the same fileId patch applies
 * identically to any activity). Every other M6 operation
 * (crop/removeInterval/removeGpsOnly/setGpsConstant/interpolateGps/
 * repairOutlier) takes an *absolute* timestamp range specific to one
 * activity's own timeline -- applying one picked range to several
 * activities recorded at different real times wouldn't mean the same
 * thing for each of them, so offering a dropdown for those here would
 * be exactly the "control wired to something that doesn't really do
 * what it claims" the project's non-negotiables rule out. `merge` is
 * excluded too, since batch-merging would need a *second* activity per
 * selected activity, which this UI has no way to specify unambiguously.
 */
export function BatchDialog({ open, onOpenChange }: BatchDialogProps) {
  // `selectBatchSelectedActivities` returns a freshly `.filter()`-ed array
  // every call, so this must go through `useShallow` -- without it,
  // Zustand's `useSyncExternalStore`-backed subscription sees a new array
  // reference on every render and re-renders forever (confirmed by a
  // real "Maximum update depth exceeded" failure while testing this
  // component, not just a theoretical concern).
  const selectedActivities = useActivityStore(useShallow(selectBatchSelectedActivities))
  const clearBatchSelection = useActivityStore((s) => s.clearBatchSelection)
  const applyOperationToIds = useActivityStore((s) => s.applyOperationToIds)
  const notify = useNotificationStore((s) => s.notify)

  const [editKind, setEditKind] = useState<EditKind>('shiftTime')
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward')
  const [hours, setHours] = useState('0')
  const [minutes, setMinutes] = useState('0')
  const [seconds, setSeconds] = useState('0')
  const [manufacturer, setManufacturer] = useState('')
  const [applying, setApplying] = useState(false)

  const [format, setFormat] = useState<ExportFormat>('fit')
  const [results, setResults] = useState<Record<string, ExportOutcome> | null>(null)
  const [exporting, setExporting] = useState(false)
  const [bundling, setBundling] = useState(false)

  // Reset generated export results whenever the format changes or the
  // dialog reopens -- same render-time-adjustment pattern as
  // `ExportDialog`'s identical reset (see its doc comment for why).
  const [prevKey, setPrevKey] = useState(`${open}:${format}`)
  const key = `${open}:${format}`
  if (key !== prevKey) {
    setPrevKey(key)
    setResults(null)
  }

  if (selectedActivities.length === 0) return null

  const h = Number(hours)
  const m = Number(minutes)
  const s = Number(seconds)
  const shiftValid = [h, m, s].every((n) => Number.isFinite(n) && n >= 0)
  const magnitudeMs = (h * 3600 + m * 60 + s) * 1000
  const offsetMs = direction === 'forward' ? magnitudeMs : -magnitudeMs
  const shiftIsNoOp = shiftValid && magnitudeMs === 0

  const metadataPatch: Record<string, unknown> =
    manufacturer.trim() === '' ? {} : { manufacturer: manufacturer.trim() }
  const metadataHasChanges = Object.keys(metadataPatch).length > 0

  const editOperation: Operation | null =
    editKind === 'shiftTime'
      ? shiftValid && !shiftIsNoOp
        ? { type: 'shiftTime', offsetMs }
        : null
      : metadataHasChanges
        ? { type: 'editMetadata', patch: metadataPatch }
        : null

  const handleApplyEdit = async () => {
    if (editOperation == null) return
    setApplying(true)
    try {
      await applyOperationToIds(
        selectedActivities.map((a) => a.id),
        editOperation,
      )
      onOpenChange(false)
    } finally {
      setApplying(false)
    }
  }

  const handleGenerateExports = async () => {
    setExporting(true)
    try {
      // Per-item try/catch, with `setResults` after every iteration
      // (rather than once at the end) -- one activity's export failing
      // must never discard results already computed for the others, and
      // a failure here gets exactly the same "blocked, here's why"
      // display every other export failure in this app already uses
      // (`exportFailureOutcome`), not a second error-display path.
      const outcomes: Record<string, ExportOutcome> = {}
      for (const loaded of selectedActivities) {
        try {
          const { activity } = await currentActivityAsync(loaded.history)
          outcomes[loaded.id] = await exportActivity(
            activity,
            format,
            loaded.fileName,
            loaded.fieldDefsByMesgNum ?? new Map(),
            loaded.history,
          )
        } catch (error) {
          outcomes[loaded.id] = exportFailureOutcome(format, loaded.fileName, error)
        }
        setResults({ ...outcomes })
      }
    } finally {
      setExporting(false)
    }
  }

  const downloadableResults =
    results == null
      ? []
      : selectedActivities
          .map((a) => results[a.id])
          .filter((o): o is ExportOutcome => o != null && o.data != null)

  const handleDownloadZip = async () => {
    if (downloadableResults.length === 0) return
    setBundling(true)
    try {
      const zipBytes = await bundleFilesToZipAsync(
        downloadableResults.map((o) => ({ name: o.fileName, data: o.data as Uint8Array | string })),
      )
      downloadBytes(zipBytes, `batch-export-${format}.zip`, EXPORT_MIME_TYPES.zip)
    } catch (error) {
      notify({
        variant: 'error',
        title: 'Could not bundle the ZIP download',
        description: error instanceof Error ? error.message : String(error),
      })
    } finally {
      setBundling(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby="batch-description" className="max-w-lg">
        <DialogTitle>Batch: {selectedActivities.length} activities</DialogTitle>
        <DialogDescription id="batch-description">
          {selectedActivities.map((a) => a.fileName).join(', ')}
        </DialogDescription>

        <Tabs defaultValue="edit">
          <TabsList>
            <TabsTrigger value="edit">Edit</TabsTrigger>
            <TabsTrigger value="export">Export</TabsTrigger>
          </TabsList>

          <TabsContent value="edit" className="flex flex-col gap-3">
            <div role="radiogroup" aria-label="Batch operation" className="flex gap-4 text-sm">
              <label className="text-foreground flex items-center gap-2">
                <input
                  type="radio"
                  name="batch-edit-kind"
                  checked={editKind === 'shiftTime'}
                  onChange={() => setEditKind('shiftTime')}
                />
                Shift time
              </label>
              <label className="text-foreground flex items-center gap-2">
                <input
                  type="radio"
                  name="batch-edit-kind"
                  checked={editKind === 'editMetadata'}
                  onChange={() => setEditKind('editMetadata')}
                />
                Edit metadata
              </label>
            </div>

            {editKind === 'shiftTime' ? (
              <div className="flex flex-col gap-2">
                <div role="radiogroup" aria-label="Direction" className="flex gap-4 text-sm">
                  <label className="text-foreground flex items-center gap-2">
                    <input
                      type="radio"
                      name="batch-shift-direction"
                      checked={direction === 'forward'}
                      onChange={() => setDirection('forward')}
                    />
                    Forward (later)
                  </label>
                  <label className="text-foreground flex items-center gap-2">
                    <input
                      type="radio"
                      name="batch-shift-direction"
                      checked={direction === 'backward'}
                      onChange={() => setDirection('backward')}
                    />
                    Backward (earlier)
                  </label>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <BatchNumberField
                    id="batch-hours"
                    label="Hours"
                    value={hours}
                    onChange={setHours}
                  />
                  <BatchNumberField
                    id="batch-minutes"
                    label="Minutes"
                    value={minutes}
                    onChange={setMinutes}
                  />
                  <BatchNumberField
                    id="batch-seconds"
                    label="Seconds"
                    value={seconds}
                    onChange={setSeconds}
                  />
                </div>
                {!shiftValid && (
                  <p className="text-destructive text-xs">Enter non-negative whole numbers.</p>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <label htmlFor="batch-manufacturer" className="text-foreground text-xs font-medium">
                  Manufacturer
                </label>
                <input
                  id="batch-manufacturer"
                  type="text"
                  value={manufacturer}
                  onChange={(e) => setManufacturer(e.target.value)}
                  placeholder="e.g. garmin"
                  className="border-input bg-background text-foreground focus-visible:ring-ring rounded-md border px-2 py-1 text-sm outline-none focus-visible:ring-2"
                />
                <p className="text-muted-foreground text-xs">
                  Applied to every ticked activity&apos;s file-identification record.
                </p>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={editOperation == null || applying}
                onClick={() => void handleApplyEdit()}
              >
                Apply to {selectedActivities.length}{' '}
                {selectedActivities.length === 1 ? 'activity' : 'activities'}
              </Button>
            </DialogFooter>
          </TabsContent>

          <TabsContent value="export" className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <label htmlFor="batch-export-format" className="text-foreground text-xs font-medium">
                Format
              </label>
              <select
                id="batch-export-format"
                value={format}
                onChange={(e) => setFormat(e.target.value as ExportFormat)}
                className="border-input bg-background text-foreground focus-visible:ring-ring rounded-md border px-2 py-1 text-sm outline-none focus-visible:ring-2"
              >
                {FORMATS.map((f) => (
                  <option key={f} value={f}>
                    {EXPORT_FORMAT_LABELS[f]}
                  </option>
                ))}
              </select>
            </div>

            <Button
              size="sm"
              variant="outline"
              disabled={exporting}
              onClick={() => void handleGenerateExports()}
              className="w-fit"
            >
              {exporting ? 'Generating…' : results == null ? 'Generate' : 'Regenerate'}
            </Button>

            {results != null && (
              // `role="status"` so a screen-reader user hears each
              // activity's result as it's generated/regenerated, rather
              // than this list silently appearing/changing.
              <ul role="status" className="flex flex-col gap-1 text-xs">
                {selectedActivities.map((a) => {
                  const outcome = results[a.id]
                  if (outcome == null) return null
                  return (
                    <li
                      key={a.id}
                      className="border-border bg-background flex items-center justify-between gap-2 rounded-md border px-2 py-1"
                    >
                      <span className="min-w-0 flex-1 truncate">
                        <span className="text-foreground">{a.fileName}</span>
                        {outcome.blocked ? (
                          <span className="text-destructive ml-1">
                            -- blocked: {outcome.validationErrors.map((e) => e.message).join(' ')}
                          </span>
                        ) : outcome.warnings.length > 0 ? (
                          <span className="text-warning-text ml-1">
                            -- {outcome.warnings.length}{' '}
                            {outcome.warnings.length === 1 ? 'note' : 'notes'}
                          </span>
                        ) : null}
                      </span>
                      {outcome.data != null && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            downloadBytes(
                              outcome.data as Uint8Array | string,
                              outcome.fileName,
                              outcome.mimeType,
                            )
                          }
                        >
                          Download
                        </Button>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}

            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              <Button
                size="sm"
                disabled={downloadableResults.length === 0 || bundling}
                onClick={() => void handleDownloadZip()}
              >
                {bundling ? 'Bundling…' : `Download all as ZIP (${downloadableResults.length})`}
              </Button>
            </DialogFooter>
          </TabsContent>
        </Tabs>

        <button
          type="button"
          onClick={() => {
            clearBatchSelection()
            onOpenChange(false)
          }}
          className="text-muted-foreground hover:text-foreground mt-2 w-fit text-xs underline"
        >
          Clear batch selection
        </button>
      </DialogContent>
    </Dialog>
  )
}

function BatchNumberField({
  id,
  label,
  value,
  onChange,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-foreground text-xs font-medium">
        {label}
      </label>
      <input
        id={id}
        type="number"
        min={0}
        step={1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border-input bg-background text-foreground focus-visible:ring-ring rounded-md border px-2 py-1 text-sm outline-none focus-visible:ring-2"
      />
    </div>
  )
}
